/**
 * @fileoverview Extraction Category Definitions
 *
 * Purpose: Defines the 15 extraction categories used for category-specific
 * metadata extraction. These are DISTINCT from the 15 document upload categories
 * — they represent the structured data schemas that summary agents consume.
 *
 * A single document can trigger multiple extraction categories (e.g., a ROCAP
 * triggers director_disclosures + assets + employment + general_liabilities).
 *
 * Used by:
 * - convex/jobs/gatherInformation/classification/ (classifier assigns categories)
 * - convex/jobs/gatherInformation/metadata/ (category-specific extractors)
 * - convex/jobs/gatherInformation/extraction/ (pipeline orchestration)
 * - Frontend components (extraction display)
 */

// ---------------------------------------------------------------------------
// Extraction Category Definitions
// ---------------------------------------------------------------------------

/**
 * The 15 extraction categories. Each has a dedicated extraction schema and prompt.
 * NOT identical to the 15 document upload categories.
 *
 * general_ledgers is separate from financial_ledgers because GL Detail reports
 * are high-volume (often 100K+ tokens) and need summary-level extraction
 * rather than line-item extraction.
 */
export const EXTRACTION_CATEGORIES = [
  "corporate_documents",
  "director_disclosures",
  "assets",
  "securities",
  "financial_ledgers",
  "general_ledgers",
  "bank_statements_transactions",
  "employment",
  "general_liabilities",
  "contracts",
  "invoices",
  "board_minutes_reports",
  "loans",
  "asset_sales_purchases",
  "tax_and_sro",
  "proof_of_debt",
] as const;

export type ExtractionCategory = (typeof EXTRACTION_CATEGORIES)[number];

/** Set for O(1) validation lookups. */
const VALID_EXTRACTION_CATEGORIES = new Set<string>(EXTRACTION_CATEGORIES);

// ---------------------------------------------------------------------------
// Extraction Category Config (label, icon, colour for UI)
// ---------------------------------------------------------------------------

export interface ExtractionCategoryConfigEntry {
  label: string;
  /** Lucide icon name */
  icon: string;
  /** Tailwind colour class stem */
  colour: string;
}

export const EXTRACTION_CATEGORY_CONFIG: Record<
  ExtractionCategory,
  ExtractionCategoryConfigEntry
> = {
  corporate_documents: {
    label: "Corporate Documents",
    icon: "Building2",
    colour: "slate",
  },
  director_disclosures: {
    label: "Director Disclosures",
    icon: "UserCheck",
    colour: "indigo",
  },
  assets: { label: "Assets", icon: "Package", colour: "teal" },
  securities: { label: "Security Interests", icon: "Shield", colour: "purple" },
  financial_ledgers: {
    label: "Financial Ledgers",
    icon: "BarChart3",
    colour: "blue",
  },
  general_ledgers: {
    label: "General Ledgers",
    icon: "BookOpen",
    colour: "sky",
  },
  bank_statements_transactions: {
    label: "Bank Transactions",
    icon: "Landmark",
    colour: "cyan",
  },
  employment: { label: "Employment", icon: "Briefcase", colour: "violet" },
  general_liabilities: {
    label: "General Liabilities",
    icon: "Users",
    colour: "orange",
  },
  contracts: { label: "Contracts", icon: "FileSignature", colour: "pink" },
  invoices: { label: "Invoices", icon: "FileSpreadsheet", colour: "yellow" },
  board_minutes_reports: {
    label: "Board Minutes & Reports",
    icon: "ClipboardList",
    colour: "emerald",
  },
  loans: { label: "Loans", icon: "Banknote", colour: "green" },
  asset_sales_purchases: {
    label: "Asset Sales & Purchases",
    icon: "ArrowLeftRight",
    colour: "amber",
  },
  tax_and_sro: { label: "Tax & SRO", icon: "Receipt", colour: "rose" },
  proof_of_debt: {
    label: "Proof of Debt",
    icon: "ClipboardCheck",
    colour: "rose",
  },
};

// ---------------------------------------------------------------------------
// Document Category → Extraction Categories Mapping (Static Fallback)
// ---------------------------------------------------------------------------

/**
 * Static fallback mapping from document upload category to extraction categories.
 * Used when the LLM fails to return valid extraction categories during classification.
 */
export const DOC_CATEGORY_TO_EXTRACTION_CATEGORIES: Record<
  string,
  ExtractionCategory[]
> = {
  corporate_documents: ["corporate_documents", "board_minutes_reports"],
  director_disclosures: [
    "director_disclosures",
    "assets",
    "employment",
    "general_liabilities",
  ],
  assets: ["assets", "asset_sales_purchases"],
  security_interests: ["securities"],
  bank_statements_transactions: ["bank_statements_transactions"],
  financial_ledgers: ["financial_ledgers", "assets", "asset_sales_purchases"],
  general_ledgers: ["general_ledgers", "assets", "asset_sales_purchases"],
  forecasts_budget: ["financial_ledgers"],
  general_liabilities: ["general_liabilities"],
  contracts: ["contracts"],
  invoices: ["invoices"],
  employment: ["employment"],
  tax_and_sro: ["tax_and_sro"],
  // proof_of_debt keeps general_liabilities alongside the dedicated POD
  // extraction: the employment agent reads employee-related claims from the
  // general_liabilities payload, while the proof_of_debt extraction feeds
  // Recoveries POD claim ingestion (MOR-784).
  proof_of_debt: ["general_liabilities", "proof_of_debt"],
  loans: ["loans", "securities", "general_liabilities"],
  legal_disputes: ["general_liabilities"],
  insurance: ["general_liabilities"],
  firm_generated: [],
  other: [],
};

// ---------------------------------------------------------------------------
// Validation Helpers
// ---------------------------------------------------------------------------

/** Returns true if the given string is one of the 15 extraction categories. */
export function isValidExtractionCategory(
  category: string,
): category is ExtractionCategory {
  return VALID_EXTRACTION_CATEGORIES.has(category);
}

/**
 * Validates and filters an array of extraction category strings.
 * Returns only valid extraction categories, deduped.
 */
export function validateExtractionCategories(
  categories: unknown,
): ExtractionCategory[] {
  if (!Array.isArray(categories)) return [];
  const seen = new Set<string>();
  const result: ExtractionCategory[] = [];
  for (const cat of categories) {
    if (
      typeof cat === "string" &&
      isValidExtractionCategory(cat) &&
      !seen.has(cat)
    ) {
      seen.add(cat);
      result.push(cat);
    }
  }
  return result;
}

/**
 * Returns extraction categories for a document, using LLM-assigned categories
 * with fallback to static mapping if none are valid.
 */
export function resolveExtractionCategories(
  llmCategories: unknown,
  documentCategory: string,
): ExtractionCategory[] {
  const validated = validateExtractionCategories(llmCategories);
  if (validated.length > 0) return validated;
  return DOC_CATEGORY_TO_EXTRACTION_CATEGORIES[documentCategory] ?? [];
}
